/**
 * Texte brut d'une carte, sans la syntaxe Markdown : question d'une ligne de la gestion des
 * cartes, recherche sans accents ni majuscules.
 */
import type { CarteMere } from '../cartes/carte-mere';
import { analyserTrous } from '../cartes/trous';
import { positionIndice } from './syntaxe';

/** Retire la syntaxe d'un texte Markdown de carte (marques, liens, images, trous). */
export function texteBrut(markdown: string, trous = false): string {
  let texte = markdown;
  if (trous) {
    // {{n::réponse::indice}} → réponse
    let resultat = '';
    let curseur = 0;
    for (const trou of analyserTrous(texte)) {
      const indice = positionIndice(trou.reponse);
      resultat +=
        texte.slice(curseur, trou.debut) +
        (indice === -1 ? trou.reponse : trou.reponse.slice(0, indice));
      curseur = trou.fin;
    }
    texte = resultat + texte.slice(curseur);
  }
  return texte
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/==\{[^}]*\}/g, '')
    .replace(/(\*\*|__|~~|==|\*|_|`|\$\$|\$)/g, '')
    .replace(/^\s{0,3}(#{1,6}\s+|>\s?|[-*+]\s+|\d+[.)]\s+)/gm, '')
    .replace(/```[^\n]*\n?/g, '')
    .replace(/\\([\\`*_{}[\]()#+\-.!$=~])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Forme de comparaison pour la recherche : sans accents, en minuscules. */
export function normaliser(texte: string): string {
  return texte.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr');
}

/** Question d'une carte fille, en texte brut, telle que la liste des cartes l'affiche. */
export function questionDeLaCarte(carte: CarteMere, cle: string): string {
  switch (carte.type) {
    case 'basique':
      return texteBrut(cle === 'verso' ? carte.contenu.verso : carte.contenu.recto);
    case 'trous': {
      const numero = cle.startsWith('trou:') ? Number(cle.slice(5)) : null;
      let resultat = '';
      let curseur = 0;
      const texte = carte.contenu.texte;
      for (const trou of analyserTrous(texte)) {
        const demande = numero === null || trou.numero === numero;
        resultat +=
          texte.slice(curseur, trou.debut) + (demande ? '[…]' : trou.reponse.split('::')[0]);
        curseur = trou.fin;
      }
      return texteBrut(resultat + texte.slice(curseur));
    }
    case 'image': {
      // Ce qui distingue les cartes vient en premier : la liste les tronque à droite.
      const zones = carte.contenu.zones;
      const i = zones.findIndex((z) => `zone:${z.id}` === cle);
      return i === -1
        ? 'Toutes les zones (masquage d’image)'
        : `Zone ${i + 1} sur ${zones.length} (masquage d’image)`;
    }
    case 'tableau': {
      const lignes = carte.contenu.lignes;
      const entetes = lignes[0]
        ?.map((c) => texteBrut(c.contenu))
        .filter(Boolean)
        .join(', ');
      const tableau = entetes ? `tableau ${entetes}` : 'tableau';
      const grisees = lignes.flat().filter((c) => c.grisee);
      const i = grisees.findIndex((c) => `case:${c.id}` === cle);
      if (i === -1) return `Toutes les cases (${tableau})`;
      // La case demandée, nommée par sa ligne (1re case de la ligne) et sa colonne (1re ligne).
      const l = lignes.findIndex((ligne) => ligne.some((c) => c.id === grisees[i]!.id));
      const c = lignes[l]!.findIndex((cellule) => cellule.id === grisees[i]!.id);
      const nomLigne = c > 0 ? texteBrut(lignes[l]![0]!.contenu) : '';
      const nomColonne = l > 0 ? texteBrut(lignes[0]![c]?.contenu ?? '') : '';
      const nom = [nomLigne, nomColonne].filter(Boolean).join(' · ');
      return `${nom || `Case ${i + 1}`} (${tableau})`;
    }
  }
}

/** Tout le texte d'une carte mère, pour la recherche. */
export function texteDeLaCarte(carte: CarteMere): string {
  switch (carte.type) {
    case 'basique':
      return `${texteBrut(carte.contenu.recto)} ${texteBrut(carte.contenu.verso)}`;
    case 'trous':
      return texteBrut(carte.contenu.texte, true);
    case 'image':
      return '';
    case 'tableau':
      return carte.contenu.lignes
        .flat()
        .map((c) => texteBrut(c.contenu))
        .join(' ');
  }
}
